
// This service now routes browser calls to the local API server.
const isBrowser = typeof window !== 'undefined' && typeof window.fetch === 'function';

const getApiBase = () => {
  // If you run the API server on a different port, update this value.
  return isBrowser ? '' : 'http://localhost:4000';
};

// Minimal compatibility helpers so this file compiles in both browser and server builds.
const Modality = {
  AUDIO: 'AUDIO'
} as const;

const getApiKey = () => {
  if (!isBrowser) return process.env.GEMINI_API_KEY || '';
  return (window as any).aistudio?.getSelectedApiKey?.() || '';
};

const getAI = async () => {
  if (isBrowser) {
    throw new Error('getAI() is server-only. Use the /api endpoints from the browser.');
  }
  const mod = await import('@google/genai');
  const GoogleGenAI = mod.GoogleGenAI;
  return new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
};

/**
 * Enhanced Chat with Thinking Budget for Pro Users
 */
export const chatWithGeminiStream = async (
  message: string,
  imageBase64: string | undefined,
  isPro: boolean,
  systemInstruction: string,
  onChunk: (text: string) => void
) => {
  if (isBrowser) {
    // Browser: call local API server
    const resp = await fetch(`${getApiBase()}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, isPro, systemInstruction })
    });
    const json = await resp.json();
    const text = json.text || json?.response?.text || '';
    onChunk(text);
    return { text, sources: [] };
  }

  // Server-side handling (if used server-side) will dynamically import the SDK
  const mod = await import('@google/genai');
  const GoogleGenAI = mod.GoogleGenAI;
  const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
  const modelName = isPro ? 'gemini-3-pro-preview' : 'gemini-3-flash-preview';
  const contents: any = { parts: [{ text: message }] };
  if (imageBase64) contents.parts.push({ inlineData: { mimeType: 'image/png', data: imageBase64 } });
  const config: any = { systemInstruction, tools: [{ googleSearch: {} }] };
  if (isPro) config.thinkingConfig = { thinkingBudget: 16000 };
  const response = await ai.models.generateContent({ model: modelName, contents, config });
  return { text: response.text || '', sources: [] };
};

export const generateImage = async (prompt: string, isPro = false) => {
  if (isPro) {
    const hasKey = await (window as any).aistudio?.hasSelectedApiKey();
    if (!hasKey) {
      await (window as any).aistudio?.openSelectKey();
    }
  }

  const ai = await getAI();
  const modelName = isPro ? 'gemini-3-pro-image-preview' : 'gemini-2.5-flash-image';
  
  const response = await ai.models.generateContent({
    model: modelName,
    contents: { parts: [{ text: prompt }] },
    config: { 
      imageConfig: { 
        aspectRatio: "1:1", 
        ...(isPro ? { imageSize: "2K" } : {}) 
      } 
    }
  });

  for (const part of response.candidates?.[0]?.content?.parts || []) {
    if (part.inlineData) return `data:image/png;base64,${part.inlineData.data}`;
  }
  throw new Error("No image generated");
};

export const generateVideo = async (prompt: string, onStatus: (status: string) => void) => {
  const hasKey = await (window as any).aistudio?.hasSelectedApiKey();
  if (!hasKey) {
    await (window as any).aistudio?.openSelectKey();
  }

  const ai = await getAI();
  let operation = await ai.models.generateVideos({
    model: 'veo-3.1-fast-generate-preview',
    prompt: prompt,
    config: { 
      numberOfVideos: 1, 
      resolution: '1080p', 
      aspectRatio: '16:9' 
    }
  });

  onStatus('Thinking...');
  while (!operation.done) {
    await new Promise(resolve => setTimeout(resolve, 10000));
    operation = await ai.operations.getVideosOperation({ operation: operation });
    onStatus('Generating your masterpiece...');
  }

  const downloadLink = operation.response?.generatedVideos?.[0]?.video?.uri;
  return `${downloadLink}&key=${getApiKey()}`;
};

export const searchGrounding = async (query: string, isPro: boolean) => {
  const ai = await getAI();
  const model = isPro ? 'gemini-3-pro-preview' : 'gemini-3-flash-preview';
  
  const response = await ai.models.generateContent({
    model: model,
    contents: query,
    config: { 
      tools: [{ googleSearch: {} }] 
    }
  });

  return {
    text: response.text || '',
    sources: response.candidates?.[0]?.groundingMetadata?.groundingChunks?.map((chunk: any) => ({
      title: chunk.web?.title || 'Source',
      uri: chunk.web?.uri || '#'
    })) || []
  };
};

export const mapsGrounding = async (query: string, location?: { lat: number; lng: number }) => {
  const ai = await getAI();
  const config: any = {
    tools: [{ googleMaps: {} }],
  };
  if (location) {
    config.toolConfig = {
      retrievalConfig: {
        latLng: {
          latitude: location.lat,
          longitude: location.lng
        }
      }
    };
  }

  const response = await ai.models.generateContent({
    model: "gemini-2.5-flash",
    contents: query,
    config
  });

  return {
    text: response.text || '',
    sources: response.candidates?.[0]?.groundingMetadata?.groundingChunks?.map((chunk: any) => ({
      title: chunk.maps?.title || 'Location',
      uri: chunk.maps?.uri || '#'
    })) || []
  };
};

export const generateSpeech = async (text: string, voiceName: string = 'Kore') => {
  const ai = await getAI();
  const response = await ai.models.generateContent({
    model: "gemini-2.5-flash-preview-tts",
    contents: [{ parts: [{ text }] }],
    config: {
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        voiceConfig: {
          prebuiltVoiceConfig: { voiceName },
        },
      },
    },
  });
  return response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
};

export const translateText = async (text: string, from: string, to: string, isPro: boolean) => {
  const ai = await getAI();
  const model = isPro ? 'gemini-3-pro-preview' : 'gemini-3-flash-preview';
  const systemPrompt = `You are a professional linguist. Translate the following text from ${from} to ${to}. Provide only the translated text. Maintain tone and cultural context perfectly.`;
  
  const response = await ai.models.generateContent({
    model: model,
    contents: text,
    config: { systemInstruction: systemPrompt }
  });
  return response.text;
};

export const dictionaryLookup = async (word: string) => {
  const ai = await getAI();
  const systemPrompt = `You are the Zomi Lexicon Expert. Provide a comprehensive entry for "${word}".
  Structure your response clearly with these sections:
  1. Phonetics & Part of Speech
  2. Definitions (Short and precise)
  3. Morphology (Break down the word roots if Zomi)
  4. Cultural Significance (Provide historical or cultural context for the word in Zomi society)
  5. Usage Examples (Format this section as a Markdown table with headers: Language, Sentence, Translation).

  For the table:
  - Language: English or Zomi
  - Sentence: The example sentence
  - Translation: The translated sentence or context

  Use high-quality Zomi language.`;
  
  const response = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: word,
    config: { systemInstruction: systemPrompt }
  });
  return response.text;
};
